<template>
  <div class="questionnaire">
    <div class="header">
      <div class="header-inner">
        <div class="simple-badge">
          <p>簡単</p>
          <p>15秒</p>
        </div>
        <div class="header-content">
          <span class="sp-simple-badge">簡単15秒！<br></span>
          <p class="header-title">
            条件から<span class="highlight">最適な専門業者</span>を探す
          </p>
        </div>
      </div>
    </div>

    <div class="question-body">
      <!-- Progress -->
      <div v-if="!showResult && !loading" class="progress-bar">
        <div class="q-badge" :class="{ current: currentSlide === 1 }">Q1</div>
        <div class="q-badge" :class="{ current: currentSlide === 2 }">Q2</div>
        <div class="q-badge" :class="{ current: currentSlide === 3 }">Q3</div>
        <div class="q-badge" :class="{ current: currentSlide === 4 }">Q4</div>
        <div class="q-badge" :class="{ current: currentSlide === 5 }">Q5</div>
      </div>

      <!-- 質問 -->
      <div v-if="currentSlide === 1 && !loading && !showResult">
        <h2 class="question-title">Q1 お住まいのエリアは？</h2>
        <div class="options-grid">
          <button v-for="option in q1Options" :key="option" class="option-button"
            :class="{ active: answers[1] === option }" @click="selectAnswer(1, option)">
            {{ option }}
          </button>
        </div>
      </div>

      <div v-if="currentSlide === 2 && !loading && !showResult">
        <h2 class="question-title">Q2 設置する建物の種類は？</h2>
        <div class="options-grid">
          <button v-for="option in q2Options" :key="option" class="option-button"
            :class="{ active: answers[2] === option }" @click="selectAnswer(2, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 3 && !loading && !showResult">
        <h2 class="question-title">Q3 希望のメーカーは？</h2>
        <div class="options-grid">
          <button v-for="option in q3Options" :key="option" class="option-button"
            :class="{ active: answers[3] === option }" @click="selectAnswer(3, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 4 && !loading && !showResult">
        <h2 class="question-title">Q4 工事希望のスピードは？</h2>
        <div class="options-grid">
          <button v-for="option in q4Options" :key="option" class="option-button"
            :class="{ active: answers[4] === option }" @click="selectAnswer(4, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 5 && !loading && !showResult">
        <h2 class="question-title">Q5 重視したいポイントは？<br>（複数選択可）</h2>
        <div class="options-grid">
          <button v-for="option in q5Options" :key="option" class="option-button"
            :class="{ active: selectedOptions.includes(option) }" :disabled="isDisabledOption(option)"
            @click="toggleOption(option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group double">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
          <button class="submit-button" @click="submit">診断結果を見る</button>
        </div>
      </div>

      <!-- ローディング -->
      <div v-if="loading" class="loading-box">
        <div class="spinner"></div>
        <p>あなたにぴったりの業者を診断中...</p>
      </div>

      <!-- 結果 -->
      <div v-if="showResult" class="result-box">
        <h2 class="section-title">あなたが選択した内容</h2>
        <ul class="answers-list">
          <li><strong>エリア :</strong> {{ answers[1] }}</li>
          <li><strong>建物の種類 :</strong> {{ answers[2] }}</li>
          <li><strong>メーカー :</strong> {{ answers[3] }}</li>
          <li><strong>工事スピード :</strong> {{ answers[4] }}</li>
          <li><strong>重視ポイント :</strong> {{ answers[5].join('、') }}</li>
        </ul>

        <h2 class="section-title">おすすめの業者</h2>
        <div class="vendor-list">
          <div v-for="vendor in recommendedVendors" :key="vendor.name" class="vendor-card">
            <h3>{{ vendor.name }}</h3>
            <p class="vendor-rank" :class="['rank-' + vendor.rank]">
              {{ vendor.caption }}
            </p>
            <img :src="vendor.image" :alt="vendor.name" class="vendor-img" />
            <ul class="vendor-features">
              <li v-for="(feature, idx) in vendor.features" :key="idx">{{ feature }}</li>
            </ul>
            <a :href="vendor.link" target="_blank" rel="nofollow noopener" class="vendor-button">公式サイトを見る</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import vendor1 from '@/assets/rank-table/vendor-1.png'
import vendor2 from '@/assets/rank-table/vendor-2.png'
import vendor3 from '@/assets/rank-table/vendor-3.png'
import { makeAffiliateUrl } from '@/utils/aff.js'

const currentSlide = ref(1)
const answers = ref({ 1: '', 2: '', 3: '', 4: '', 5: [] })

const q1Options = ['北海道', '東北', '関東', '東海', '近畿', '中国', '四国', '九州']
const q2Options = ['戸建て', 'マンション']
const q3Options = ['ノーリツ', 'リンナイ', 'パロマ', 'パーパス', 'ハーマン', 'その他', '特になし']
const q4Options = ['とにかく最短', '1週間以内', '未定']
const q5Options = ['費用の安さ', '施工実績', '口コミ評価', '保証内容', '対応スピード', '特になし']

const selectedOptions = ref([])
const loading = ref(false)
const showResult = ref(false)
const recommendedVendors = ref([])

const selectAnswer = (qNum, value) => { answers.value[qNum] = value; currentSlide.value++ }
const backSlide = () => { if (currentSlide.value > 1) currentSlide.value-- }

const toggleOption = (option) => {
  if (selectedOptions.value.includes(option)) {
    selectedOptions.value = selectedOptions.value.filter(o => o !== option)
  } else {
    if (option === '特になし') {
      selectedOptions.value = ['特になし']
    } else {
      selectedOptions.value = selectedOptions.value.filter(o => o !== '特になし')
      selectedOptions.value.push(option)
    }
  }
  answers.value[5] = selectedOptions.value
}
const isDisabledOption = (option) => {
  if (option === '特になし' && selectedOptions.value.length > 0 && !selectedOptions.value.includes('特になし')) return true
  if (selectedOptions.value.includes('特になし') && option !== '特になし') return true
  return false
}

const submit = () => {
  loading.value = true
  showResult.value = false
  setTimeout(() => {
    loading.value = false
    showResult.value = true
    recommendedVendors.value = calculateVendors()
  }, 2000)
}

const calculateVendors = () => {
  const affiliateLink = makeAffiliateUrl()
  const vendors = [
    {
      name: '給湯器駆けつけ隊',
      image: vendor1,
      link: affiliateLink,
      features: ['業界最安値クラス！', '最短30分で駆けつけ！', 'お客様満足度97.3%！']
    }
  ]
  if (
    !['北海道', '東北', '近畿', '四国', '九州'].includes(answers.value[1]) &&
    !answers.value[5].includes('口コミ評価') &&
    !answers.value[5].includes('施工実績') &&
    !answers.value[5].includes('保証内容')
  ) {
    vendors.push({
      name: '街角給湯相談所',
      image: vendor3,
      link: 'https://lp.machikado-sodan.com/ab/XpBiwJMXRLixOgatw',
      features: ['本体の割引率が最大92%！', '資格を持ったプロが対応！', '対応メーカーが豊富！']
    })
  }
  if (answers.value[4] !== 'とにかく最短' && !answers.value[5].includes('費用の安さ')) {
    vendors.push({
      name: 'キンライサー',
      image: vendor2,
      link: 'https://www.kyutooki.com',
      features: ['全国対応！', '年間7万件以上の施工実績', '安全性の高い工事！']
    })
  }

  const captions = ['あなたに最もおすすめ！', '２番目におすすめ！', 'こちらも検討価値あり！']

  return vendors.map((vendor, index) => ({
    ...vendor,
    rank: index + 1,
    caption: captions[index] || 'おすすめ業者'
  }))
}
</script>

<style scoped>
.loading-box {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #3f51b5;
  margin: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3f51b5;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.result-box {
  border-radius: 8px;
  background: #fff;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  color: #3f51b5;
  border-bottom: 2px solid #3f51b5;
  padding-bottom: 5px;
  margin: 20px 0 15px;
  margin-top: 0;
}

.answers-list {
  list-style: none;
  padding: 15px;
  margin: 0 0 20px;
  background: #f5f7fb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.answers-list li {
  background: #fff;
  border: 1px solid #ddd;
  padding: 10px 12px;
  font-size: 15px;
  color: #333;
  text-align: left;
}

.answers-list li strong {
  color: #3f51b5;
  font-weight: 700;
  margin-right: 6px;
  min-width: 90px;
}

.answers-list li::before {
  content: "✔";
  color: #3f51b5;
  font-weight: bold;
  margin-right: 10px;
}

.vendor-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-top: 15px;
}

.vendor-card {
  background: #f5f7fb;
  border: 1px solid #ddd;
  padding: 15px;
  text-align: center;
  /* border-radius: 8px; */
}

.vendor-card h3 {
  margin: 0;
  margin-bottom: 4px;
  font-size: 1.2rem;
  font-weight: bold;
}

.vendor-img {
  max-width: 100%;
  margin: 0 auto 10px;
}

.vendor-features {
  list-style: none;
  padding: 0;
  margin: 10px 0 15px;
  text-align: left;
  font-size: 14px;
  color: #333;
}

.vendor-features li {
  margin-bottom: 6px;
}

.vendor-features li::before {
  content: "✔";
  margin-right: 6px;
  color: #3f51b5;
}

.vendor-button {
  display: inline-block;
  margin-top: 0;
  padding: 8px 15px;
  background-color: #3f51b5;
  color: white;
  text-decoration: none;
  font-weight: bold;
  border-radius: 6px;
}

.vendor-button:hover {
  background-color: #5c6bc0;
}

/* 金銀銅バッジ */
.vendor-rank {
  font-weight: bold;
  margin: 6px 0 10px;
  font-size: 14px;
  display: inline-block;
  padding: 4px 12px;
  border-radius: 10px;
  color: #fff;
}

.rank-1 {
  background: #d7a50d;
}

.rank-2 {
  background: #c0c0c0;
}

.rank-3 {
  background: #cd7f32;
}

/* 質問部分 */
.questionnaire {
  border: 1px solid;
  margin: 30px;
}

.header {
  position: relative;
  background-color: #3f51b5;
  color: #fff;
  padding: 14px;
  text-align: center;
}

.simple-badge {
  position: absolute;
  top: -20px;
  left: -24px;
  background: #ffcb00;
  font-weight: bold;
  font-size: 14px;
  padding: 12px 16px;
  border-radius: 50%;
  text-align: center;
  width: 60px;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: rotate(-10deg);
  z-index: 10;
  color: black;
}

.simple-badge p {
  font-size: 20px;
  margin: 0;
  line-height: 1;
}

.header-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  color: #fff;
}

.highlight {
  color: #ffcb00;
  font-size: 27px;
}

.progress-bar {
  display: flex;
  justify-content: center;
}

.q-badge {
  width: 40px;
  height: 40px;
  background-color: #9c9c9c;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: #fff;
  margin: 0 5px;
}

.q-badge.current {
  background-color: #ffc200;
}

.question-body {
  background-color: #fff;
  padding: 30px;
}

.question-title {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  color: #333;
  margin-top: 10px;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.option-button {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #ff7e0e;
  background: #ffa50024;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
}

.option-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.option-button.active {
  border-color: #ffc200;
  background-color: #fff;
  color: #ffc200;
}

.button-group {
  display: flex;
  justify-content: center;
}

.button-group.double {
  justify-content: space-between;
  gap: 10px;
}

.back-button,
.submit-button {
  height: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  flex: 1;
}

.back-button {
  background-color: #f0f0f0;
  color: #666;
  border: 1px solid #ccc;
}

.back-button:hover {
  background-color: #e0e0e0;
}

.submit-button {
  background-color: #3f51b5;
  color: white;
  border: none;
}

.submit-button:hover {
  background-color: #5c6bc0;
}

.sp-simple-badge {
  display: none;
}

/* スマホ対応 */
@media (max-width: 600px) {
  .sp-simple-badge {
    display: inline-block;
    background-color: #fff;
    color: #3f51b5;
    padding: 5px 20px;
    font-weight: bold;
    border-radius: 10px;
    font-size: 1.2rem;
  }

  .questionnaire {
    margin: 10px;
  }

  .header-title {
    font-size: 1.1rem;
    margin-top: 5px;
  }

  .highlight {
    font-size: 1.2rem;
  }

  .question-title {
    font-size: 1rem;
  }

  .option-button {
    min-height: 2.2rem;
    font-size: 1rem;
  }

  .simple-badge {
    display: none;
  }

  .q-badge {
    width: 60px;
    height: 40px;
    font-size: 1rem;
  }

  .options-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .vendor-card {
    margin-top: 10px;
    border-radius: none;
  }

  .vendor-card h3 {
    font-size: 1.2rem;
  }

  .vendor-button {
    font-size: 1rem;
    padding: 15px 30px;
    border-radius: 10px;
  }

  .vendor-features {
    font-size: 0.9rem;
  }
}
</style>
